import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { type ComponentProps } from 'react'
import {
  CSSProperties,
  ComponentPropsWithoutRef,
  useEffect,
  useState,
} from 'react'

import { DatoStructuredText } from '@/features/common/'
import { DatoLink } from '@/features/dato-link'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface RangeSliderProps extends ComponentPropsWithoutRef<'input'> {}

const RangeSlider = ({ style, ...props }: RangeSliderProps) => {
  const styles = {
    wrapper: css`
      position: relative;
      width: 100%;
      padding-bottom: 20px;
    `,
    input: css`
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 24px;
      background: transparent;
      outline: none;
      cursor: pointer;

      &::-webkit-slider-runnable-track {
        width: 100%;
        height: 5px;
        border-radius: 10px;
        background: linear-gradient(
          to right,
          ${colors.red} var(--percent, 0%),
          ${colors.gray85} var(--percent, 0%)
        );
      }

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 26px;
        height: 18px;
        margin-top: -8px;
        border-radius: 50px;
        background: #fff;
        border: 2px solid #727272;
        position: relative;
        z-index: 1;
      }

      &::-moz-range-track {
        width: 100%;
        height: 5px;
        border-radius: 10px;
        background: linear-gradient(
          to right,
          ${colors.red} var(--percent, 0%),
          ${colors.gray85} var(--percent, 0%)
        );
      }

      &::-moz-range-thumb {
        appearance: none;
        width: 26px;
        height: 18px;
        margin-top: -8px;
        border-radius: 50px;
        background: #fff;
        border: 2px solid #727272;
      }
    `,
    labels: css`
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: var(--fs-18);
      color: ${colors.gray30};
      position: relative;
      margin-top: -11px;
      margin-inline: 12px;
    `,
    label: css`
      position: relative;
      span {
        position: absolute;
        top: 100%;
        width: 100%;
        text-align:center;
        left:-24px;
      font-size: var(--fs-14);
      }
      &::before {
        content: '';s
        position: relative;
        display: block;
        margin-inline: auto;
        width: 2px;
        height: 18px;
        background: #d8d8d8;
        border-radius: 2px;
        z-index: 0;
      }
    `,
  }

  return (
    <div css={styles.wrapper}>
      <input
        type="range"
        css={styles.input}
        style={style}
        {...props}
      />
      <div css={styles.labels}>
        <div css={styles.label}>
          <span>$0</span>
        </div>
        <div css={styles.label}>
          <span>$50K</span>
        </div>
        <div css={styles.label}>
          <span>$100K</span>
        </div>
        <div css={styles.label}>
          <span>$150K</span>
        </div>
        <div css={styles.label}>
          <span>$200K</span>
        </div>
      </div>
    </div>
  )
}

interface Props extends ComponentProps<'section'> {
  data: Queries.TuitionCalculatorFragment | null
  layout?: string | null
}

export const TuitionCalculator = ({
  data,
  layout,
  ...props
}: Props) => {
  const [income, setIncome] = useState(100)
  const min = 0
  const max = 200
  let financialAid = '100%'
  let tuition = '$0'

  switch (true) {
    case income <= 100:
      financialAid = '100%'
      tuition = '$0'
      break
    case income <= 125:
      financialAid = '99% to 100%'
      tuition = '$0 to $500'
      break
    case income <= 150:
      financialAid = '98% to 100%'
      tuition = '$0 to $1500'
      break
    case income <= 175:
      financialAid = '97% to 100%'
      tuition = '$0 to $2000'
      break
    case income <= 200:
      financialAid = '88% to 100%'
      tuition = '$0 to $10,000'
      break
    default:
      financialAid = '88% to 100%'
      tuition = '$0 to $10,000'
  }

  const percent = ((income - min) / (max - min)) * 100

  const styles = {
    wrapper: css`
      background: ${layout == 'INLINE' ? '#fff' : colors.gray95};
      ${layout !== 'INLINE' &&
      css`
        padding-inline: var(--margin);
        padding-block-end: var(--row-144);
      `}
    `,
    inner: css`
      margin-inline: ${data?.alternativeLayout ? 0 : '2em'};
      text-align: center;
      background: ${data?.alternativeLayout ? colors.gray95 : '#fff'};
      padding: 60px clamp(50px, 10vw, 200px);
    `,
    title: css`
      font-family: var(--ff-display);
      font-size: var(--fs-60);
      line-height: 1.13;
      color: ${colors.gray10};
      margin: 0 0 0.5em;
    `,
    layoutWrap: css`
      display: ${data?.alternativeLayout ? 'flex' : 'block'};
      align-items: center;
      justify-content: center;
      gap: 1em;
      margin-bottom: 2em;
<<<<<<< HEAD
      ${mq().l} {
=======
      ${mq().s} {
>>>>>>> a24872bf4b90ec13ef6c21e5529201e4276874a2
        display: block;
      }
    `,
    question: css`
      font-family: var(--ff-display);
      font-size: var(--fs-36);
      line-height: 1.13;
      color: #000;
      margin: 0 0 ${data?.alternativeLayout ? 0 : '1em'};
<<<<<<< HEAD
      flex-shrink: 0;
=======
>>>>>>> a24872bf4b90ec13ef6c21e5529201e4276874a2
    `,
    annualIncome: css`
      font-family: var(--ff-body);
      font-size: var(--fs-60);
      font-weight: 800;
      line-height: 1.5;
      color: ${colors.gray20};
      display: inline-flex;
      align-items: center;
    `,
    incomeValue: css`
      border-bottom: 2px solid #d0d0d0;
      padding-inline: 20px;
    `,
    dollarSign: css`
      color: #bababa;
    `,
    sliderWrap: css`
      margin: 0 0 2em;
    `,
    freeTuitionLink: css`
      display: inline-flex;
      align-items: center;
      gap: 0.4em;
      font-family: var(--ff-body);
      font-size: var(--fs-16);
      font-weight: 450;
      text-transform: uppercase;
      color: ${colors.red};
      text-decoration: none;
      margin-bottom: 1em;
      &::after {
        content: '';
        width: 9px;
        height: 14px;
        background-image: url("data:image/svg+xml,%3Csvg width='9' height='14' viewBox='0 0 9 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6.76416 6.76416L0.999998 12.5283' stroke='%23D92636' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A");
      }
    `,
    results: css`
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--gtr-36);
      margin-bottom: 2em;

      ${mq().s} {
        grid-template-columns: 1fr;
      }
    `,
    resultCard: css`
      background: ${data?.alternativeLayout ? '#fff' : colors.gray95};
      padding-inline: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 137px;
    `,
    resultLabel: css`
      display: block;
      font-family: var(--ff-body);
      font-size: var(--fs-16);
      text-transform: uppercase;
      color: ${colors.gray45};
      margin-bottom: 0.5em;
    `,
    resultValue: css`
      display: block;
      font-family: var(--ff-display);
      font-size: var(--fs-36);
      line-height: 1.13;
      color: ${colors.gray30};
    `,
    disclosure: css`
      font-size: var(--fs-14);
      line-height: 1.5;
      color: ${colors.gray30};
      max-width: 648px;
      margin: 0 auto 1.5em;
    `,
    tableLink: css`
      text-decoration: underline;
      display: flex;
      justify-content: center;
      span {
        font-size: var(--fs-16);
        font-weight: 600;
        color: ${colors.gray30};
        text-transform: none !important;
      }
    `,
  }

  const incomeLabel =
    income >= max ? '200,000' : `${income.toLocaleString('en-US')},000`

  return (
    <section
      {...props}
      css={styles.wrapper}
    >
      {data?.alternativeLayout && (
        <h2>What You Can Expect To Pay at Brearley</h2>
      )}
      <div css={styles.inner}>
        {!data?.alternativeLayout && (
          <h2 css={styles.title}>Cost Breakdown</h2>
        )}

        <div css={styles.layoutWrap}>
          <p css={styles.question}>
            {data?.alternativeLayout ? (
              <span>My total annual household income is:</span>
            ) : (
              <span> What is your total annual household income?</span>
            )}
          </p>

          <div css={styles.annualIncome}>
            <span css={styles.dollarSign}>$</span>
            <span css={styles.incomeValue}>{incomeLabel}</span>
          </div>
        </div>

        <div css={styles.sliderWrap}>
          <RangeSlider
            min={min}
            max={max}
            step={10}
            value={income}
            onChange={event =>
              setIncome(parseInt(event.target.value, 10))
            }
            style={{ '--percent': `${percent}%` } as CSSProperties}
            aria-label="Annual household income"
          />
        </div>

        <div css={styles.results}>
          <div css={styles.resultCard}>
            <span css={styles.resultLabel}>
              Financial aid would cover
            </span>
            <span css={styles.resultValue}>{financialAid} tuition</span>
          </div>
          <div css={styles.resultCard}>
            <span css={styles.resultLabel}>Your family would pay</span>
            <span css={styles.resultValue}>{tuition} tuition</span>
          </div>
        </div>

        <a
          href="/tuition-free"
          css={styles.freeTuitionLink}
        >
          View our free tuition page
        </a>

        <DatoStructuredText data={data?.calculatorDisclaimer} />

        <div css={styles.tableLink}>
          <DatoLink data={data?.tuitionTableLink} />
        </div>
      </div>
    </section>
  )
}

export const TuitionCalculatorFragment = graphql`
  fragment TuitionCalculator on DatoCmsTuitionCalculator {
    __typename
    id: originalId
    showCalculator
    alternativeLayout
    calculatorDisclaimer {
      value
    }
    tuitionTableLink {
      __typename
      linkText
      article {
        slug
      }
    }
  }
`
